import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { Person, CalendarToday, Launch, ContentCopy, Folder, Assignment } from '@mui/icons-material';
import { ActiveCourse } from '../../../types/activeCourses.types';

interface CourseCardProps {
  course: ActiveCourse;
  onJoinClass: (joinUrl: string) => void;
  onCopyGuid: (courseGuid: string) => void;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, onJoinClass, onCopyGuid }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6" component="h3">
            {course.courseType}
          </Typography>
          <Chip 
            label={course.courseVersion}
            size="small"
            color={course.courseVersion === 'v2.x' ? 'success' : 'warning'}
            variant="outlined"
          />
        </Box>
        
        {/* Course GUID with copy button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {course.courseGuid}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<ContentCopy />}
            onClick={() => onCopyGuid(course.courseGuid)}
            sx={{ 
              minWidth: 'auto', 
              px: 1, 
              py: 0.5,
              fontSize: '0.75rem',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            Copy
          </Button>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <CalendarToday sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            Course Schedule:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {course.courseDatesWithTutors.map((dateWithTutor, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label={formatDate(dateWithTutor.date)} 
                  size="small" 
                  variant={dateWithTutor.isPastDate ? "filled" : "outlined"}
                  color={dateWithTutor.isPastDate ? "default" : "primary"}
                  sx={{ 
                    minWidth: '120px',
                    opacity: dateWithTutor.isPastDate ? 0.6 : 1 
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {dateWithTutor.tutorEmail}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <Person sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            Assigned Tutors:
          </Typography>
          {course.tutors.map((tutor, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {tutor.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tutor.sessionCount} session{tutor.sessionCount !== 1 ? 's' : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
      
      <Box sx={{ p: 2, pt: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Launch />}
            onClick={() => onJoinClass(course.joinUrl)}
            sx={{ fontSize: '0.75rem', px: 1 }}
          >
            Join Class
          </Button>
          <Button
            variant="outlined"
            startIcon={<Folder />}
            onClick={() => course.driveUrl && window.open(course.driveUrl, '_blank')}
            disabled={!course.driveUrl}
            sx={{ fontSize: '0.75rem', px: 1 }}
          >
            Drive
          </Button>
          <Button
            variant="outlined"
            startIcon={<Assignment />}
            onClick={() => course.registerUrl && window.open(course.registerUrl, '_blank')}
            disabled={!course.registerUrl}
            sx={{ fontSize: '0.75rem', px: 1 }}
          >
            Register
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default CourseCard;
